import { useCallback, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
import type { Area } from 'react-easy-crop';
import { ApiRequestError } from '@/api/client';
import { getCroppedImg } from '@/lib/imageCrop';
import { SubpageHeader } from '@/components/ui/SubpageHeader';

export interface ImageCropPreviewSize {
  width: number;
  height: number;
  label: string;
  badgeClass: string;
  imageClass: string;
}

export interface ImageCropUploadScreenProps {
  titleSelect: string;
  titleCrop: string;
  titlePreview: string;
  uploadPrompt: string;
  uploadHint: string;
  previewHeading: string;
  previewSubheading: string;
  saveLabel: string;
  confirmLabel: string;
  aspect?: number;
  cropShape?: 'round' | 'rect';
  minZoom?: number;
  maxZoom?: number;
  capture?: 'user' | 'environment';
  previewSizes: ImageCropPreviewSize[];
  onUpload: (blob: Blob) => Promise<string>;
  onSuccess: (uploadedUrl: string) => void;
  onBack: () => void;
}

const DEFAULT_MIN_ZOOM = 1;
const DEFAULT_MAX_ZOOM = 3;

export function ImageCropUploadScreen({
  titleSelect,
  titleCrop,
  titlePreview,
  uploadPrompt,
  uploadHint,
  previewHeading,
  previewSubheading,
  saveLabel,
  confirmLabel,
  aspect = 1,
  cropShape = 'round',
  minZoom = DEFAULT_MIN_ZOOM,
  maxZoom = DEFAULT_MAX_ZOOM,
  capture,
  previewSizes,
  onUpload,
  onSuccess,
  onBack,
}: ImageCropUploadScreenProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(minZoom);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [croppedPreview, setCroppedPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(null), 2500);
  };

  const resetCropState = () => {
    setImageSrc(null);
    setCroppedAreaPixels(null);
    setCroppedPreview(null);
    setZoom(minZoom);
    setCrop({ x: 0, y: 0 });
  };

  const onCropComplete = useCallback((_: Area, croppedPixels: Area) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file');
      return;
    }
    const reader = new FileReader();
    reader.addEventListener('load', () => {
      if (typeof reader.result === 'string') {
        setImageSrc(reader.result);
        setCroppedPreview(null);
        setZoom(minZoom);
        setCrop({ x: 0, y: 0 });
      }
    });
    reader.readAsDataURL(file);
  };

  const handlePreview = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      setCroppedPreview(URL.createObjectURL(blob));
    } catch {
      showToast('Failed to crop image');
    }
  };

  const handleSave = async () => {
    if (!imageSrc || !croppedAreaPixels) return;
    setSaving(true);
    try {
      const blob = await getCroppedImg(imageSrc, croppedAreaPixels);
      const uploadedUrl = await onUpload(blob);
      onSuccess(uploadedUrl);
    } catch (err) {
      const msg = err instanceof ApiRequestError ? err.message : 'Failed to save image';
      showToast(msg);
      setSaving(false);
    }
  };

  const handleBack = () => {
    if (imageSrc && !croppedPreview) {
      resetCropState();
      return;
    }
    if (croppedPreview) {
      setCroppedPreview(null);
      return;
    }
    onBack();
  };

  const title = croppedPreview ? titlePreview : imageSrc ? titleCrop : titleSelect;

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      <SubpageHeader
        title={title}
        onBack={handleBack}
        right={
          imageSrc && !croppedPreview ? (
            <button
              type="button"
              onClick={() => void handlePreview()}
              className="text-green-600 text-sm font-semibold cursor-pointer whitespace-nowrap"
            >
              Preview
            </button>
          ) : null
        }
      />

      <div className="flex-1 flex flex-col">
        {!imageSrc && (
          <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6">
            <div
              className={`w-24 h-24 flex items-center justify-center bg-green-50 border-2 border-dashed border-green-200 ${
                cropShape === 'round' ? 'rounded-full' : 'rounded-lg'
              }`}
            >
              <i className="ri-image-add-line text-green-500 text-3xl"></i>
            </div>
            <div className="text-center">
              <div className="text-gray-900 font-bold text-lg">{uploadPrompt}</div>
              <div className="text-gray-400 text-sm mt-1 whitespace-pre-line">{uploadHint}</div>
            </div>
            <div className="w-full space-y-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-11 bg-green-500 hover:bg-green-600 text-white font-normal text-sm rounded-lg cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2"
              >
                <i className="ri-gallery-line text-base"></i>
                Choose from Gallery
              </button>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-11 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-normal text-sm rounded-lg cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2"
              >
                <i className="ri-camera-line text-base"></i>
                Take a Photo
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture={capture}
              className="hidden"
              onChange={handleFileChange}
            />
          </div>
        )}

        {imageSrc && !croppedPreview && (
          <div className="flex-1 flex flex-col">
            <div className="flex-1 relative bg-gray-900 overflow-hidden" style={{ minHeight: 320 }}>
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                cropShape={cropShape}
                showGrid={false}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            </div>

            <div className="bg-white px-4 py-2.5 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <i className="ri-zoom-out-line text-gray-400 text-lg"></i>
                <input
                  type="range"
                  min={minZoom}
                  max={maxZoom}
                  step={0.05}
                  value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  className="flex-1 h-1.5 bg-gray-200 rounded-full appearance-none cursor-pointer accent-green-500"
                />
                <i className="ri-zoom-in-line text-gray-400 text-lg"></i>
              </div>
              <div className="text-center text-gray-400 text-xs mt-2">
                Drag to move · Pinch or use slider to zoom
              </div>
            </div>

            <div className="bg-white px-4 py-2.5 border-t border-gray-100 flex gap-3">
              <button
                type="button"
                onClick={resetCropState}
                className="flex-1 h-11 rounded-lg border border-gray-200 text-gray-600 font-normal text-sm cursor-pointer whitespace-nowrap hover:bg-gray-50 transition-colors"
              >
                Retake
              </button>
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={!croppedAreaPixels || saving}
                className="flex-1 h-11 rounded-lg bg-green-500 text-white font-normal text-sm cursor-pointer whitespace-nowrap hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    Saving…
                  </>
                ) : (
                  <>
                    <i className="ri-check-line text-base"></i>
                    {saveLabel}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {croppedPreview && (
          <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6">
            <div className="text-center">
              <div className="text-gray-900 font-bold text-lg">{previewHeading}</div>
              <div className="text-gray-400 text-sm mt-1">{previewSubheading}</div>
            </div>

            <div className="flex flex-col items-center gap-6">
              {previewSizes.length > 0 && (
                <div className="relative">
                  <img
                    src={croppedPreview}
                    alt={`Preview ${previewSizes[0].label}`}
                    className={previewSizes[0].imageClass}
                    style={{ width: previewSizes[0].width, height: previewSizes[0].height }}
                  />
                  <span
                    className={`absolute -bottom-1 left-1/2 -translate-x-1/2 text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${previewSizes[0].badgeClass}`}
                  >
                    {previewSizes[0].label}
                  </span>
                </div>
              )}
              {previewSizes.length > 1 && (
                <div className="flex items-center gap-6">
                  {previewSizes.slice(1).map((size) => (
                    <div key={size.label} className="relative">
                      <img
                        src={croppedPreview}
                        alt={`Preview ${size.label}`}
                        className={size.imageClass}
                        style={{ width: size.width, height: size.height }}
                      />
                      <span
                        className={`absolute -bottom-1 left-1/2 -translate-x-1/2 text-white text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${size.badgeClass}`}
                      >
                        {size.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="w-full space-y-3">
              <button
                type="button"
                onClick={() => void handleSave()}
                disabled={saving}
                className="w-full h-11 bg-green-500 hover:bg-green-600 text-white font-normal text-sm rounded-lg cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base"></i>
                    Saving…
                  </>
                ) : (
                  <>
                    <i className="ri-check-double-line text-base"></i>
                    {confirmLabel}
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setCroppedPreview(null)}
                className="w-full h-11 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 font-normal text-sm rounded-lg cursor-pointer whitespace-nowrap transition-colors flex items-center justify-center gap-2"
              >
                <i className="ri-scissors-cut-line text-base"></i>
                Crop Again
              </button>
            </div>
          </div>
        )}
      </div>

      {toast && (
        <div className="fixed top-16 left-0 right-0 z-50 flex justify-center px-4">
          <div className="bg-gray-900 text-white text-sm font-medium px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2">
            <i className="ri-error-warning-line text-amber-400"></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
